import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useCanvasDimensions, useFloorSchemaImage } from 'features/WarehouseMapConstructor';
import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box, useGetWarehouseBoxesQuery } from 'entities/Box';
import { RentOptionDocument } from 'entities/RentOption';
import { WarehouseForBooking } from 'entities/Warehouse';
import { UserAccountType } from 'entities/User';
import { getSelectedFilter } from 'features/BoxSizeFilter';
import { Button } from 'shared/ui/Button';
import { getBookingFloorMapData } from '../utils/helpers/getBookingFloorMapData';
import { getBookingMapCenteringSourceNode } from '../utils/helpers/getBookingMapCenteringSourceNode';
import { type BookingMapUnpickableTapKind } from '../utils/helpers/getBookingMapUnpickableTapKind';
import { showBoxNotification } from '../utils/helpers/showBookingMapUnpickableTapNotification';
import { useBookingMapStageState } from '../utils/hooks/useBookingMapStageState';
import { useSelectedFloor } from '../utils/hooks/useSelectedFloor';
import { useStagePanTween } from '../utils/hooks/useStagePanTween';
import { BookingMapDiscountBanner, BookingMapPriceBlock } from './BookingMapSelectedBoxPricing';
import { BookingMapBoxPreviewCard } from './BookingMapBoxPreviewCard';
import { BookingMapShapes } from './BookingMapShapes';

interface WarehouseMapProps {
  selectedWarehouseId: string | undefined;
  selectedBoxId: string | undefined;
  rentOptions?: RentOptionDocument[];
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onBookBox: (box: Box, sizeCodeId: string) => void;
  centerOnSelected?: boolean;
  highlightOnlySelected?: boolean;
  allowedSizeCodeId?: string;
  warehouseInfo?: Nullable<WarehouseForBooking>;
  accountType?: Nullable<UserAccountType>;
  isShowPromotionPriceNeeded?: boolean;
}

type ColorType = 'free' | 'occupied' | 'selected' | 'different' | 'disabled';

const STAGE_HEIGHT = 500;

const BOOKING_MAP_MIN_ZOOM = 0.3;
const BOOKING_MAP_MAX_ZOOM = 2;
const BOOKING_MAP_ZOOM_STEP = 1.15;

const BOOKING_MAP_PREVIEW_GAP = 8;
const BOOKING_MAP_PREVIEW_MAX_CARD_WIDTH_PX = 380;
const BOOKING_MAP_PREVIEW_ESTIMATED_HEIGHT = 260;

const clampBookingMapScale = (value: number): number => Math.min(BOOKING_MAP_MAX_ZOOM, Math.max(BOOKING_MAP_MIN_ZOOM, value));

export const WarehouseMap: FC<WarehouseMapProps> = memo((props) => {
  const {
    selectedWarehouseId,
    selectedBoxId,
    rentOptions,
    onSelectBox: onSelectBoxProp,
    onBookBox,
    centerOnSelected = true,
    highlightOnlySelected = false,
    allowedSizeCodeId,
    warehouseInfo,
    accountType,
    isShowPromotionPriceNeeded = false,
  } = props;

  const { t } = useAppTranslation('booking');

  const stageRef = useRef<Konva.Stage>(null);
  const { stagePosition, stageScale, stagePositionRef, stageScaleRef, setStagePosition, setStageScale } = useBookingMapStageState();

  const { stageParentRef, canvasDimensions } = useCanvasDimensions();

  const { image, loadImageSrc } = useFloorSchemaImage();

  const { data: floors } = useGetFloorsByWarehouseIdQuery(selectedWarehouseId, { skip: !selectedWarehouseId });
  const { data: boxes } = useGetWarehouseBoxesQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const selectedFilter = useAppSelector(getSelectedFilter);

  const selectedBox = useMemo(() => boxes?.find((box) => box.boxId === selectedBoxId), [boxes, selectedBoxId]);
  const selectedBoxFloorId = useMemo(
    () => selectedBox?.floorId ?? selectedBox?.floor?.floorId,
    [selectedBox?.floor?.floorId, selectedBox?.floorId],
  );

  const { selectedFloor, setSelectedFloor } = useSelectedFloor({ floors, selectedBoxId, selectedBoxFloorId });

  const renderedFloor = useMemo(() => {
    return floors?.find((floor) => floor.floorId === selectedFloor);
  }, [floors, selectedFloor]);

  const floorSchemaImage = renderedFloor?.floorSchemaBackgroundUrl;

  const selectBox = useCallback(
    (box: Box, sizeCodeId: string): void => {
      onSelectBoxProp(box, sizeCodeId);
    },
    [onSelectBoxProp],
  );

  const notifyBookingMapUnpickableTap = useCallback(
    (kind: BookingMapUnpickableTapKind): void => {
      showBoxNotification(kind, t);
    },
    [t],
  );

  const applyStageCursor = useCallback((cursor: string): void => {
    const container = stageRef.current?.container();

    if (container) {
      container.style.cursor = cursor;
    }
  }, []);

  const { animateStagePositionTo, clearPanTween } = useStagePanTween({
    positionRef: stagePositionRef,
    setPosition: setStagePosition,
  });

  const isSelectedBoxOccupied = Boolean(selectedBox?.contractNumber || selectedBox?.reservationNumber);

  const handleBookBox = useCallback((): void => {
    if (!selectedBox || isSelectedBoxOccupied || !selectedBox.isActive) return;

    onBookBox(selectedBox, selectedBox.sizeCode.sizeCodeId);
  }, [isSelectedBoxOccupied, onBookBox, selectedBox]);

  const isSelectedBoxBookable = Boolean(selectedBox?.isActive && !isSelectedBoxOccupied);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [boxPreviewLayout, setBoxPreviewLayout] = useState<{
    centerX: number;
    topY: number;
    bottomY: number;
    placement: 'above' | 'below';
    cardMaxWidthPx: number;
  } | null>(null);

  const updateBoxPreviewLayout = useCallback((): void => {
    const parent = stageParentRef.current;
    const stage = stageRef.current;

    if (!parent || !stage || !selectedBoxId || !selectedBox || !warehouseInfo) {
      setBoxPreviewLayout(null);

      return;
    }

    if (!selectedBox.isActive || isSelectedBoxOccupied) {
      setBoxPreviewLayout(null);

      return;
    }

    if (selectedBoxFloorId && selectedFloor && selectedBoxFloorId !== selectedFloor) {
      setBoxPreviewLayout(null);

      return;
    }

    const matches = stage.find((node: Konva.Node) => node.attrs?.id === selectedBoxId);
    const target =
      (matches.find((node) => node.getType() === 'Group') as Konva.Group | undefined) ?? (matches[0] as Konva.Node | undefined);

    if (!target) {
      setBoxPreviewLayout(null);

      return;
    }

    const rectSource = getBookingMapCenteringSourceNode(target) ?? target;
    const rect = rectSource.getClientRect({ relativeTo: stage });
    const pos = { x: stage.x(), y: stage.y() };
    const scale = stage.scaleX();

    const topY = pos.y + rect.y * scale;
    const bottomY = pos.y + (rect.y + rect.height) * scale;
    const centerXRaw = pos.x + (rect.x + rect.width / 2) * scale;

    const parentH = parent.clientHeight;
    const parentW = parent.clientWidth;
    const spaceAbove = topY;
    const spaceBelow = parentH - bottomY;
    const needed = BOOKING_MAP_PREVIEW_ESTIMATED_HEIGHT + BOOKING_MAP_PREVIEW_GAP;
    const placement = spaceAbove >= needed || spaceAbove >= spaceBelow ? 'above' : 'below';

    const margin = 8;
    const innerWidth = Math.max(0, parentW - 2 * margin);
    const cardMaxWidthPx = Math.min(BOOKING_MAP_PREVIEW_MAX_CARD_WIDTH_PX, innerWidth);
    const half = cardMaxWidthPx / 2;
    const centerX = Math.min(Math.max(centerXRaw, margin + half), parentW - margin - half);

    setBoxPreviewLayout({
      centerX,
      topY,
      bottomY,
      placement,
      cardMaxWidthPx,
    });
  }, [isSelectedBoxOccupied, selectedBox, selectedBoxFloorId, selectedBoxId, selectedFloor, stageParentRef, warehouseInfo]);

  useEffect(() => {
    setIsPreviewOpen(Boolean(selectedBoxId));
  }, [selectedBoxId]);

  useLayoutEffect(() => {
    updateBoxPreviewLayout();

    const stage = stageRef.current;

    if (!stage) {
      return undefined;
    }

    const onDragMove = (): void => {
      updateBoxPreviewLayout();
    };

    stage.on('dragmove', onDragMove);

    return (): void => {
      stage.off('dragmove', onDragMove);
    };
  }, [updateBoxPreviewLayout, stagePosition, stageScale, renderedFloor?.shapesConfig, image, canvasDimensions.width]);

  useEffect(() => {
    const el = stageParentRef.current;

    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(() => {
      updateBoxPreviewLayout();
    });

    ro.observe(el);

    return (): void => {
      ro.disconnect();
    };
  }, [stageParentRef, updateBoxPreviewLayout]);

  useEffect(() => {
    loadImageSrc(floorSchemaImage ?? '');
  }, [floorSchemaImage, loadImageSrc]);

  const { shapes, imageTransform, labelFontSize } = useMemo(
    () => getBookingFloorMapData({ shapesConfig: renderedFloor?.shapesConfig, mapConfig: renderedFloor?.mapConfig }),
    [renderedFloor?.mapConfig, renderedFloor?.shapesConfig],
  );
  const hasFloorMap = shapes.length > 0;

  const shapesConfigKey = renderedFloor?.shapesConfig ?? '';

  const stageWidth = Math.max(canvasDimensions.width, 1);

  const zoomAtViewportCenter = useCallback(
    (nextScale: number): void => {
      clearPanTween();

      const oldScale = stageScaleRef.current;
      const clamped = clampBookingMapScale(nextScale);

      if (clamped === oldScale) {
        return;
      }

      const pointer = { x: stageWidth / 2, y: STAGE_HEIGHT / 2 };
      const pos = stagePositionRef.current;
      const mousePointTo = {
        x: (pointer.x - pos.x) / oldScale,
        y: (pointer.y - pos.y) / oldScale,
      };
      const newPos = {
        x: pointer.x - mousePointTo.x * clamped,
        y: pointer.y - mousePointTo.y * clamped,
      };

      stageScaleRef.current = clamped;
      stagePositionRef.current = newPos;
      setStageScale(clamped);
      setStagePosition(newPos);
    },
    [clearPanTween, setStagePosition, setStageScale, stagePositionRef, stageScaleRef, stageWidth],
  );

  const zoomIn = useCallback((): void => {
    zoomAtViewportCenter(stageScaleRef.current * BOOKING_MAP_ZOOM_STEP);
  }, [stageScaleRef, zoomAtViewportCenter]);

  const zoomOut = useCallback((): void => {
    zoomAtViewportCenter(stageScaleRef.current / BOOKING_MAP_ZOOM_STEP);
  }, [stageScaleRef, zoomAtViewportCenter]);

  const { canBookingMapZoomIn, canBookingMapZoomOut } = useMemo(() => {
    const zoomInTarget = clampBookingMapScale(stageScale * BOOKING_MAP_ZOOM_STEP);
    const zoomOutTarget = clampBookingMapScale(stageScale / BOOKING_MAP_ZOOM_STEP);

    return {
      canBookingMapZoomIn: zoomInTarget > stageScale,
      canBookingMapZoomOut: zoomOutTarget < stageScale,
    };
  }, [stageScale]);

  const centerStageOnSelectedBox = useCallback((): void => {
    if (!centerOnSelected) {
      return;
    }

    const stage = stageRef.current;

    if (!selectedBoxId || !stage || stageWidth <= 1) {
      clearPanTween();
      setStagePosition({ x: 0, y: 0 });

      return;
    }

    const boxFloorId = selectedBoxFloorId;

    if (boxFloorId && selectedFloor && boxFloorId !== selectedFloor) {
      clearPanTween();
      setStagePosition({ x: 0, y: 0 });

      return;
    }

    const matches = stage.find((node: Konva.Node) => node.attrs?.id === selectedBoxId);
    const target =
      (matches.find((node) => node.getType() === 'Group') as Konva.Group | undefined) ?? (matches[0] as Konva.Node | undefined);

    if (!target) {
      clearPanTween();
      setStagePosition({ x: 0, y: 0 });

      return;
    }

    const rectSource = getBookingMapCenteringSourceNode(target) ?? target;
    const rect = rectSource.getClientRect({ relativeTo: stage });
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const w = stage.width();
    const h = stage.height();
    const scale = stageScaleRef.current;

    const nextX = w / 2 - cx * scale;
    const nextY = h / 2 - cy * scale;

    animateStagePositionTo(nextX, nextY);
  }, [
    animateStagePositionTo,
    centerOnSelected,
    clearPanTween,
    selectedBoxFloorId,
    selectedBoxId,
    selectedFloor,
    setStagePosition,
    stageScaleRef,
    stageWidth,
  ]);

  useLayoutEffect(() => {
    if (!centerOnSelected) {
      clearPanTween();
      setStagePosition({ x: 0, y: 0 });

      return;
    }

    if (!selectedBoxId) {
      clearPanTween();
      setStagePosition({ x: 0, y: 0 });

      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      centerStageOnSelectedBox();
    });

    return (): void => {
      window.cancelAnimationFrame(frameId);
    };
  }, [centerOnSelected, centerStageOnSelectedBox, clearPanTween, image, selectedBoxId, selectedFloor, setStagePosition, shapesConfigKey]);

  const resetStageContainerCursor = useCallback((): void => {
    applyStageCursor('');
  }, [applyStageCursor]);

  useEffect(() => {
    return resetStageContainerCursor;
  }, [resetStageContainerCursor]);

  const handleStageDragEnd = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      clearPanTween();
      setStagePosition({ x: e.target.x(), y: e.target.y() });
      resetStageContainerCursor();
    },
    [clearPanTween, resetStageContainerCursor, setStagePosition],
  );

  const closeBoxPreview = useCallback((): void => {
    setIsPreviewOpen(false);
  }, []);

  const handleStageEmptyPointer = useCallback(
    (e: KonvaEventObject<Event>): void => {
      if (e.target === e.target.getStage()) {
        closeBoxPreview();
      }
    },
    [closeBoxPreview],
  );

  const getLegendClass = (type: ColorType): string => {
    const commonClass = 'w-4 h-4 mr-2.5 rounded-full border';

    const colorMap: Record<ColorType, string> = {
      free: 'border-[#2BA865] bg-[#E2FEF1]',
      occupied: 'border-[#EF4444] bg-[#FEE4E2]',
      selected: 'border-[#2058E7] bg-[#DEE8FF]',
      different: 'border-[#788593] bg-[#F3F6F9]',
      disabled: 'border-[#788593] bg-[#F3F6F9]',
    };

    return `${commonClass} ${colorMap[type]}`;
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white rounded-lg overflow-hidden">
        {selectedBox ? <BookingMapDiscountBanner selectedBox={selectedBox} rentOptions={rentOptions} /> : null}
        <div className="p-7">
          <div className="grid grid-cols-1 gap-3 desktop:grid-cols-3 desktop:items-center z-50">
            <div className="font-semibold text-2xl justify-self-start">{t('Choose floor')}</div>
            <div className="flex flex-wrap items-center justify-center gap-2 justify-self-center">
              {floors?.map((floor) => (
                <div
                  key={floor.floorId}
                  className={classNames('py-1.5 px-3.5 rounded-lg text-primaryLight cursor-pointer', {
                    'bg-accent text-white': selectedFloor === floor.floorId,
                  })}
                  onClick={() => {
                    clearPanTween();
                    setSelectedFloor(floor.floorId);
                    stageScaleRef.current = 1;
                    stagePositionRef.current = { x: 0, y: 0 };
                    setStageScale(1);
                    setStagePosition({ x: 0, y: 0 });
                  }}
                >
                  {getLocalizedString(floor.name)}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 justify-self-stretch desktop:justify-self-end">
              {selectedBox ? <BookingMapPriceBlock selectedBox={selectedBox} /> : null}
              {selectedBox && isSelectedBoxBookable ? (
                <Button containerClassName="shrink-0" onClick={handleBookBox}>
                  {t('Book now')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white p-7 rounded-lg">
        <div ref={stageParentRef} className="relative min-h-[500px] w-full">
          {hasFloorMap ? (
            <>
              <Stage
                ref={stageRef}
                width={stageWidth}
                height={STAGE_HEIGHT}
                x={stagePosition.x}
                y={stagePosition.y}
                scaleX={stageScale}
                scaleY={stageScale}
                draggable
                onDragEnd={handleStageDragEnd}
                onMouseLeave={resetStageContainerCursor}
                onClick={handleStageEmptyPointer}
                onTap={handleStageEmptyPointer}
              >
                <Layer>
                  <KonvaImage
                    name="background-image"
                    image={image}
                    x={imageTransform.x}
                    y={imageTransform.y}
                    rotation={imageTransform.rotation}
                    opacity={imageTransform.opacity}
                    listening={false}
                  />
                  <BookingMapShapes
                    shapes={shapes}
                    fontSize={labelFontSize}
                    boxes={boxes}
                    selectedBoxId={selectedBoxId}
                    selectedFilter={selectedFilter}
                    allowedSizeCodeId={allowedSizeCodeId}
                    highlightOnlySelected={highlightOnlySelected}
                    onSelectBox={onSelectBoxProp}
                    onPickBox={selectBox}
                    notifyUnpickableTap={notifyBookingMapUnpickableTap}
                    applyStageCursor={applyStageCursor}
                  />
                </Layer>
              </Stage>
              {isPreviewOpen && boxPreviewLayout && selectedBox && warehouseInfo ? (
                <div
                  className="pointer-events-none absolute z-[25] box-border"
                  style={{
                    left: boxPreviewLayout.centerX,
                    width: boxPreviewLayout.cardMaxWidthPx,
                    maxWidth: boxPreviewLayout.cardMaxWidthPx,
                    top:
                      boxPreviewLayout.placement === 'above'
                        ? boxPreviewLayout.topY - BOOKING_MAP_PREVIEW_GAP
                        : boxPreviewLayout.bottomY + BOOKING_MAP_PREVIEW_GAP,
                    transform: boxPreviewLayout.placement === 'above' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
                  }}
                >
                  <BookingMapBoxPreviewCard
                    box={selectedBox}
                    warehouseInfo={warehouseInfo}
                    accountType={accountType}
                    isShowPromotionPriceNeeded={isShowPromotionPriceNeeded}
                    onClose={closeBoxPreview}
                  />
                </div>
              ) : null}
              <div className="pointer-events-none absolute right-4 top-1/2 z-10 flex -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-secondaryAccent bg-white shadow-md">
                <button
                  type="button"
                  aria-label={t('Zoom in')}
                  disabled={!canBookingMapZoomIn}
                  onClick={zoomIn}
                  className="pointer-events-auto flex h-10 w-10 min-w-[2.5rem] items-center justify-center border-0 border-b border-secondaryAccent bg-transparent p-0 text-primaryLight hover:bg-secondaryAccent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlusOutlined className="text-base" />
                </button>
                <button
                  type="button"
                  aria-label={t('Zoom out')}
                  disabled={!canBookingMapZoomOut}
                  onClick={zoomOut}
                  className="pointer-events-auto flex h-10 w-10 min-w-[2.5rem] items-center justify-center border-0 bg-transparent p-0 text-primaryLight hover:bg-secondaryAccent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MinusOutlined className="text-base" />
                </button>
              </div>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-4">
                <div className="flex items-center text-sm">
                  <div className={getLegendClass('free')} />
                  {t('Free boxes')}
                </div>
                <div className="flex items-center text-sm">
                  <div className={getLegendClass('occupied')} />
                  {t('Occupied boxes')}
                </div>
                <div className="flex items-center text-sm">
                  <div className={getLegendClass('selected')} />
                  {t('Selected boxes')}
                </div>
                <div className="flex items-center text-sm">
                  <div className={getLegendClass('different')} />
                  {t('Different size box')}
                </div>
              </div>
            </>
          ) : (
            <div className="flex min-h-[500px] items-center justify-center px-4 text-center text-lg text-primaryLight">
              {t('Currently no map is available for this floor, choose another floor or switch to list mode')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
